import { API_URL } from 'Constants'
import { useUI } from 'ctx/UIContext'
import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { getHeaders } from 'lib/apiHelpers'
import { generateRandomNumberInRange } from 'lib/misc'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const browser = typeof window !== 'undefined'

// The atom to hold the value goes here
const themeAtom = atomWithStorage(
  'theme',
  browser && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
)

// global folder name
const folderAtom = atomWithStorage('folder', '')

type Submission = any
type User = any

const submissionsAtom = atom<Submission[]>([])
const submissionAtom = atomWithStorage<Submission | null>('submission', null)
const userAtom = atomWithStorage<User>('current_user', {})
const notificationsAtom = atom<any[]>([])

/** Sitewide theme */
export function useTheme(): [string, any] {
  const [theme, setTheme] = useAtom(themeAtom)

  useEffect(() => {
    if (!browser) return

    document.body.classList.remove('light', 'dark')
    document.body.classList.add(theme)
  }, [theme])

  return [theme, setTheme]
}
export function useTitle(): [string, any, any] {
  const [title, setTitle] = useAtom(folderAtom)
  const titlePre = generateRandomNumberInRange()

  const updateTitle = (val: string) => {
    const newTitle = `${val
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .replaceAll(' ', '_')}_${titlePre.toString()}`
    setTitle(newTitle)
  }

  useEffect(() => {
    setTitle('')
  }, [])

  return [title, updateTitle, setTitle]
}

const APIRequest = async (url: string, options: RequestInit) => {
  return await fetch(`${url}`, options)
    .then((res) => {
      if (!res.ok) {
        throw new Error(res.status.toString())
      }
      return res.json()
    })
    .catch((err) => {
      if (err && err.message && err.message?.toLowerCase() === '403') {
        localStorage.clear()
        window?.location?.reload()
      }
    })
}

export function useSubmissions() {
  const [submissions, setSubmissions] = useAtom(submissionsAtom)
  const [submission, setSubmission] = useAtom(submissionAtom)
  const { setLoading } = useUI()
  const navigate = useNavigate()
  const { user } = useUser()

  const addSubmissions = async (sub: Submission) => {
    const headers = getHeaders()

    // console.log(user, headers)
    if (!headers) return
    if (!user && !user.user_id) return

    setLoading(true)

    const raw = new FormData()
    raw.append('user_id', user.user_id)
    raw.append('format', sub.type)
    raw.append('title', sub.title)
    raw.append('synopsis', sub.synopsis)
    raw.append('genre', sub.genre.join(','))
    raw.append('folder', sub.folder)
    raw.append('artwork', sub.portrait_2_3)

    if (sub.type === 'series') {
      raw.append('season', sub.season)
      raw.append('episode', sub.number_of_episodes)
    }

    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    // console.log(raw)

    return await APIRequest(`${API_URL}submission`, requestOptions)

    // .finally(() => {
    //   setLoading(false)
    // })
    // setSubmissions((subs) => [...subs, sub])
  }

  const getSubmissions = async (page?: number) => {
    const headers = getHeaders()
    if (!headers) return
    if (!user || !user.user_id) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }
    return await APIRequest(
      // `${API_URL}submission?user_id=285`,
      `${API_URL}submission?user_id=${user.user_id}${page ? `&page=${page}` : ''}`,
      requestOptions
    )
  }

  const getSubmission = async (film_id: string) => {
    const headers = getHeaders()

    if (!headers) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}submission?film_id=${film_id}`,
      requestOptions
    )
  }

  const updateSubmissions = async (page?: number) => {
    const res = await getSubmissions(page)
    if (res && 'items' in res) {
      setSubmissions(res.items)
    }
    return res
  }

  const updateSubmissionTitle = async (film_id: string, title: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = JSON.stringify({
      film_id,
      category: 'title',
      title,
    })

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'PUT',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}submission`, requestOptions)
  }

  const deleteSubmission = async (id: string) => {
    const headers = getHeaders()

    if (!headers) return

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'DELETE',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}submission?film_id=${id}`,
      requestOptions
    ).finally(() => {
      setLoading(false)
    })
  }

  const addInformation = async (data: any) => {
    const headers = getHeaders()
    if (!headers) return
    if (!user && !user.user_id) return

    setLoading(true)

    const raw = new FormData()
    raw.append('agreement_id', data.agreement_id)
    raw.append('title', data.title)
    raw.append('fullname', data.fullname)
    raw.append('email', data.email)
    raw.append('phone', data.phone)
    raw.append('address', data.address_line_1)
    raw.append('address2', data.address_line_2)
    raw.append('city', data.city)
    raw.append('country', data.country)
    raw.append('state', data.state)
    raw.append('signature', data.signature)
    raw.append('notes', data.notes)
    raw.append('agreement_file', data.agreement_file)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }
    return await APIRequest(`${API_URL}agreement`, requestOptions).finally(() => {
      setLoading(false)
    })
  }

  const getInformation = async (id: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}agreement?agreement_id=${id}`,
      requestOptions
    ).finally(() => {
      setLoading(false)
    })
  }

  const getDeliverables = async (id: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}deliverables?deliverables_id=${id}`,
      requestOptions
    )
  }

  const submitDeliverables = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return

    const raw = new FormData()
    data.deliverables_id && raw.append('deliverables_id', data.deliverables_id)
    data.closed_caption && raw.append('closed_caption', data.closed_caption)
    data.portrait_34 && raw.append('portrait_34', data.portrait_34)
    data.portrait_23 && raw.append('portrait_23', data.portrait_23)
    data.landscape_169 && raw.append('landscape_169', data.landscape_169)
    data.landscape_43 && raw.append('landscape_43', data.landscape_43)
    data.psd_artwork && raw.append('psd_artwork', data.psd_artwork)
    data.trailer && raw.append('trailer', data.trailer)
    data.full_length && raw.append('full_length', data.full_length)
    data.promotional_video && raw.append('promotional_video', data.promotional_video)
    data.vertical_promo_video &&
      raw.append('vertical_promo_video', data.vertical_promo_video)
    data.vertical_trailer && raw.append('vertical_trailer', data.vertical_trailer)
    data.trailer_captions && raw.append('trailer_captions', data.trailer_captions)
    data.trailer_metadata && raw.append('trailer_metadata', data.trailer_metadata)
    data.master_metadata && raw.append('master_metadata', data.master_metadata)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}deliverables`, requestOptions)
  }

  const getMetadata = async (id: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}metadata?metadata_id=${id}`,
      requestOptions
    ).finally(() => {
      setLoading(false)
    })
  }

  const submitMeta = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = JSON.stringify(data)

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}metadata`, requestOptions).finally(() => {
      setLoading(false)
    })
  }

  const createSeason = async (metadata_id: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = new FormData()
    raw.append('metadata_id', metadata_id)

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}season`, requestOptions).finally(() => {
      setLoading(false)
    })
  }

  const getSeason = async (metadata_id: string, season_number: string | number) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}season?metadata_id=${metadata_id}&season=${season_number}`,
      requestOptions
    )
  }

  const getEpisode = async (id: string) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}series?series_id=${id}`, requestOptions)
  }

  const submitEpisode = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = new FormData()
    raw.append('metadata_id', data.metadata_id)
    raw.append('episode_row', data.episode_row)

    raw.append('season', data.season)
    raw.append('title', data.title)
    raw.append('synopsis', data.synopsis)
    data.closed_caption && raw.append('closed_caption', data.closed_caption)
    data.cover_landscape && raw.append('cover_landscape', data.cover_landscape)
    data.full_length && raw.append('full_length', data.full_length)

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}series`, requestOptions)
  }
  const updateEpisode = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = JSON.stringify({
      series_id: data.series_id,
      episode_row: data.episode_row,
      title: data.title,
      synopsis: data.synopsis,
      closed_caption: data.closed_caption,
      cover_landscape: data.cover_landscape,
      full_length: data.full_length,
    })

    // setLoading(true)
    const requestOptions: RequestInit = {
      method: 'PUT',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}series`, requestOptions)
  }

  const deleteEpisode = async (id: any) => {
    const headers = getHeaders()

    if (!headers) return

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'DELETE',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}series?series_id=${id}`,
      requestOptions
    ).finally(() => {
      setLoading(false)
    })
  }

  const submitForReview = async (film_id: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = new FormData()
    raw.append('film_id', film_id)

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}review`, requestOptions).finally(() => {
      setLoading(false)
      navigate('/my-submissions')
    })
  }

  const getMissingItems = async (film_id: string | number) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      redirect: 'follow',
      cache: 'no-cache',
    }

    return await APIRequest(
      `${API_URL}missing-data?film_id=${film_id}`,
      requestOptions
    ).finally(() => {
      setLoading(false)
    })
  }

  return {
    submissions,
    submission,
    setSubmission,
    addSubmissions,
    getSubmissions,
    getSubmission,
    updateSubmissions,
    updateSubmissionTitle,
    deleteSubmission,
    setSubmissions,
    addInformation,
    getInformation,
    getDeliverables,
    submitDeliverables,
    getMetadata,
    submitMeta,
    getEpisode,
    getSeason,
    createSeason,
    submitEpisode,
    deleteEpisode,
    updateEpisode,
    submitForReview,
    getMissingItems,
  }
}

export function useUser() {
  const [user, setUser] = useAtom(userAtom)
  const { setLoading } = useUI()

  const getUser = async (email: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await APIRequest(`${API_URL}user?user=${email}`, requestOptions)
  }

  const updateUser = async (data: any) => {
    const headers = getHeaders()

    if (!headers) return
    if (!user && !user.user_id) return

    const raw = JSON.stringify({
      user_id: user.user_id,
      firstname: data.firstName,
      lastname: data.lastName,
      email: data.email,
      paypal: data.paypal_email,
      instagram_handle: data.instagramHandle,
      emailenable: data.notifications?.toString(),
    })

    setLoading(true)
    const requestOptions: RequestInit = {
      method: 'PUT',
      headers,
      redirect: 'follow',
      body: raw,
      cache: 'no-cache',
    }

    return await APIRequest(`${API_URL}user`, requestOptions).finally(() => {
      setLoading(false)
    })
  }

  return {
    user,
    getUser,
    setUser,
    updateUser,
  }
}

export function useAnalytics() {
  const getAnalytics = async (user_id: string, quarter: string, year: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await APIRequest(
      `${API_URL}accounting-earnings?user_id=${user_id}&quarter=${quarter}&year=${year}`,
      requestOptions
    )
  }

  const getDuePayments = async (user_id: string, quarter: string, year: string) => {
    const headers = getHeaders()
    if (!headers) return

    const requestOptions: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    return await APIRequest(
      `${API_URL}accounting-payment?filmmaker_id=${user_id}&quarter=${quarter}&year=${year}`,
      requestOptions
    )
  }

  const requestDuePayments = async (payment_id: string) => {
    const headers = getHeaders()
    if (!headers) return

    const raw = new FormData()
    raw.append('payment_id', payment_id)

    const requestOptions: RequestInit = {
      method: 'POST',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
      body: raw,
    }

    return await fetch(`${API_URL}accounting-payment`, requestOptions)
  }

  return { getAnalytics, getDuePayments, requestDuePayments }
}

export function useNotifications() {
  const { user } = useUser()

  const [notifications, setNotifications] = useAtom(notificationsAtom)

  const getNotifications = async (user_id: string) => {
    const headers = getHeaders()

    if (!headers) return

    const options: RequestInit = {
      method: 'GET',
      headers,
      cache: 'no-cache',
      redirect: 'follow',
    }

    const url = `${API_URL}notification?user_id=${user_id}`

    return await APIRequest(url, options)
  }

  const markAsRead = async (notification_id: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('notification_id', notification_id)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}notification`
    return await APIRequest(`${url}`, options)
  }

  const unsubscribe = async (user_id: string, category: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('user_id', user_id)
    raw.append('category', category)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}unsubscribe`
    return await APIRequest(`${url}`, options)
  }
  const unsubscribeNewsletter = async (email: string, category: string) => {
    const headers = getHeaders()

    if (!headers) return
    const raw = new FormData()
    raw.append('email', email)
    raw.append('category', category)

    const options: RequestInit = {
      method: 'POST',
      headers,
      body: raw,
      redirect: 'follow',
      cache: 'no-cache',
    }

    const url = `${API_URL}unsubscribe`
    return await APIRequest(`${url}`, options)
  }

  const updateNotifications = async () => {
    if (!user) return
    await getNotifications(user.user_id).then((res) => {
      if (res && 'list' in res) {
        setNotifications(res.list)
      }
    })
  }

  useEffect(() => {
    updateNotifications()
  }, [user])

  return {
    notifications,
    getNotifications,
    updateNotifications,
    markAsRead,
    unsubscribe,
    unsubscribeNewsletter,
  }
}
